import { useState } from 'react';
import SEO from '@/components/feature/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export default function AboutPage() {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const contactFormUrl = 'https://readdy.ai/api/form/d9nfg44it35ii3jdlqmg';
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      return;
    }

    if (formData.message.length > 500) {
      setFormError('留言内容不能超过500个字符');
      setFormStatus('error');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch(contactFormUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};

      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        const msg = (parsed?.meta as Record<string, string>)?.message
          || (parsed?.meta as Record<string, string>)?.detail
          || (parsed as Record<string, string>)?.message
          || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const timelineItems = [
    { year: '2018', title: '草民影院诞生', desc: '一群热爱影视的年轻人创建了草民影院，致力于为大众提供免费高清影视内容。' },
    { year: '2019', title: '资源突破5000部', desc: '平台影视资源快速增长，覆盖电影、电视剧、综艺、动漫等多个品类。' },
    { year: '2020', title: '用户突破百万', desc: '凭借优质的用户体验和丰富的影视内容，月度活跃用户突破100万。' },
    { year: '2022', title: '技术全面升级', desc: '采用最新CDN加速技术和智能推荐算法，为用户带来更流畅的观影体验。' },
    { year: '2024', title: '内容生态完善', desc: '影视资源突破10000部，每日更新，成为国内领先的免费影视平台。' },
    { year: '2025', title: 'AI智能推荐', desc: '引入AI技术实现个性化影视推荐，让每位用户都能发现心仪的内容。' },
  ];

  const values = [
    { icon: 'ri-heart-line', title: '用户至上', desc: '永远把用户体验放在第一位，倾听每一位用户的反馈和建议。' },
    { icon: 'ri-shield-check-line', title: '品质保证', desc: '精选高清影视资源，确保每部影片都达到1080P以上画质标准。' },
    { icon: 'ri-flashlight-line', title: '极速更新', desc: '7x24小时不间断更新，热门影视第一时间上线，让追剧不再等待。' },
    { icon: 'ri-global-line', title: '开放共享', desc: '打破地域限制，为全球华语用户提供免费高清影视观看服务。' },
  ];

  const aboutFAQs = [
    {
      q: '草民影院的影视资源从哪里来？',
      a: '草民影院的影视资源来自互联网公开分享的合法影视内容。我们通过技术手段聚合各大平台公开发布的免费影视资源，为用户提供便捷的一站式观看体验。我们始终尊重知识产权，如有版权问题请随时联系我们处理。',
    },
    {
      q: '草民影院如何保证视频画质？',
      a: '我们采用严格的资源筛选机制，优先收录1080P及以上高清资源。同时通过智能转码技术和多线路CDN加速，确保用户无论使用什么设备都能获得最佳的观影画质和流畅的播放体验。',
    },
    {
      q: '如何联系草民影院团队？',
      a: '您可以通过本页面的联系表单与我们取得联系，也可以发送邮件至 contact@caominyingyuan.com。我们会在24小时内回复您的咨询。对于版权相关问题，我们会优先处理。',
    },
    {
      q: '草民影院有APP吗？',
      a: '目前草民影院为纯网页版平台，支持电脑、平板、手机浏览器直接访问。我们正在积极开发移动端应用，届时会第一时间通知用户。不过您可以放心，网页版已针对移动端做了全面适配，体验流畅。',
    },
  ];

  return (
    <>
      <ScrollToTop />
      <SEO
        title="关于我们 - 草民影院 | 免费高清影视在线观看平台"
        description="草民影院成立于2018年，是国内领先的免费高清影视在线观看平台。提供免费电影、电视剧、综艺、动漫、纪录片等海量影视资源。了解我们的品牌故事、发展历程和联系方式。"
        keywords="草民影院,关于我们,免费影视平台,品牌介绍,联系方式,影视在线观看"
        canonicalUrl="https://caominyingyuan.com/about"
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <div className="pt-16 md:pt-20">
            <Breadcrumb />

            {/* Hero */}
            <section className="w-full px-4 md:px-8 py-12 md:py-20 bg-white">
              <div className="max-w-screen-2xl mx-auto text-center">
                <h1 className="text-3xl md:text-5xl font-black text-foreground-900 font-serif">
                  关于草民影院
                </h1>
                <p className="mt-4 text-sm md:text-lg text-foreground-500 max-w-3xl mx-auto leading-relaxed">
                  草民影院成立于2018年，是国内领先的免费高清影视在线观看平台。
                  我们致力于打破观影门槛，让每一位用户都能免费享受到高质量的影视内容。
                  从最初的几十部影片到现在超过10000部影视资源，我们始终坚持&ldquo;免费、高清、流畅&rdquo;的初心。
                </p>
              </div>
            </section>

            {/* Stats */}
            <section className="w-full px-4 md:px-8 py-10 bg-background-50">
              <div className="max-w-screen-2xl mx-auto">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {[
                    { num: '10000+', label: '影视资源' },
                    { num: '500万+', label: '月活用户' },
                    { num: '1080P', label: '高清画质' },
                    { num: '7年', label: '深耕行业' },
                  ].map((stat, idx) => (
                    <div key={idx} className="text-center p-6 rounded-xl bg-white">
                      <div className="text-3xl md:text-4xl font-black text-primary-500 font-serif">
                        {stat.num}
                      </div>
                      <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Values */}
            <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-white">
              <div className="max-w-screen-2xl mx-auto">
                <div className="text-center mb-10">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-serif">
                    我们的价值观
                  </h2>
                  <p className="text-sm text-foreground-500 mt-2">驱动草民影院不断前行的核心理念</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {values.map((val, idx) => (
                    <div key={idx} className="p-6 rounded-xl bg-background-50 text-center group hover:bg-background-100 transition-colors">
                      <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-4">
                        <i className={`${val.icon} text-xl`}></i>
                      </div>
                      <h3 className="font-bold text-foreground-900 mb-2">{val.title}</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{val.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Timeline */}
            <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-50">
              <div className="max-w-screen-2xl mx-auto">
                <div className="text-center mb-10">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-serif">
                    发展历程
                  </h2>
                  <p className="text-sm text-foreground-500 mt-2">草民影院的成长足迹</p>
                </div>
                <div className="max-w-3xl mx-auto">
                  {timelineItems.map((item, idx) => (
                    <div key={idx} className="flex gap-4 md:gap-6 pb-8 last:pb-0">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-white font-bold text-sm">
                          {item.year}
                        </div>
                        {idx < timelineItems.length - 1 && (
                          <div className="w-0.5 flex-1 bg-background-200 mt-2"></div>
                        )}
                      </div>
                      <div className="pt-2">
                        <h3 className="font-bold text-foreground-900">{item.title}</h3>
                        <p className="text-sm text-foreground-500 mt-1">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-white">
              <div className="max-w-screen-xl mx-auto">
                <div className="text-center mb-10">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-serif">
                    常见问题
                  </h2>
                </div>
                <div className="max-w-3xl mx-auto space-y-3">
                  {aboutFAQs.map((faq, idx) => (
                    <div key={idx} className="rounded-xl bg-background-50 overflow-hidden">
                      <button
                        onClick={() => setOpenFAQ(openFAQ === idx ? null : idx)}
                        className="w-full px-6 py-4 flex items-center justify-between text-left cursor-pointer hover:bg-background-100 transition-colors"
                      >
                        <span className="text-sm font-medium text-foreground-900 pr-4">{faq.q}</span>
                        <div className={`w-6 h-6 flex items-center justify-center rounded-full transition-all duration-300 flex-shrink-0 ${
                          openFAQ === idx ? 'bg-primary-500 text-white rotate-180' : 'bg-background-200 text-foreground-500'
                        }`}>
                          <i className="ri-arrow-down-s-line text-sm"></i>
                        </div>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${
                        openFAQ === idx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      }`}>
                        <div className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Contact Form */}
            <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-50">
              <div className="max-w-screen-2xl mx-auto">
                <div className="max-w-2xl mx-auto">
                  <div className="text-center mb-10">
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-serif">
                      联系我们
                    </h2>
                    <p className="text-sm text-foreground-500 mt-2">有任何问题或建议？请随时与我们取得联系</p>
                  </div>

                  <form
                    id="contact-form"
                    data-readdy-form=""
                    onSubmit={handleSubmit}
                    action={contactFormUrl}
                    method="POST"
                    className="bg-white rounded-xl p-6 md:p-8 space-y-5"
                  >
                    {/* Anti-spam honeypot */}
                    <input
                      type="text"
                      name="phone_alt"
                      className="hp-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />

                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">
                        您的姓名
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="请输入您的姓名"
                        className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">
                        电子邮箱
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="请输入您的电子邮箱"
                        className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1">
                        主题
                      </label>
                      <input
                        id="contact-subject"
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        placeholder="请输入主题"
                        className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">
                        留言内容（最多500字）
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        maxLength={500}
                        placeholder="请输入您想说的话..."
                        className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
                    </div>

                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {formStatus === 'submitting' ? '发送中...' : '发送留言'}
                    </button>

                    {formStatus === 'success' && (
                      <div className="text-center p-3 rounded-lg bg-secondary-50 text-secondary-700 text-sm">
                        <i className="ri-check-line mr-1"></i>
                        感谢您的留言！我们会尽快与您联系。
                      </div>
                    )}

                    {formStatus === 'error' && formError && (
                      <div className="text-center p-3 rounded-lg bg-primary-50 text-primary-700 text-sm">
                        <i className="ri-error-warning-line mr-1"></i>
                        {formError}
                      </div>
                    )}
                  </form>

                  {/* Contact Info */}
                  <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[
                      { icon: 'ri-mail-line', title: '邮箱', content: 'contact@caominyingyuan.com' },
                      { icon: 'ri-time-line', title: '工作时间', content: '7x24小时在线' },
                      { icon: 'ri-map-pin-line', title: '所在地', content: '中国 · 北京' },
                    ].map((info, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-4 rounded-xl bg-white">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 flex-shrink-0">
                          <i className={`${info.icon} text-lg`}></i>
                        </div>
                        <div>
                          <div className="text-xs text-foreground-400">{info.title}</div>
                          <div className="text-sm font-medium text-foreground-900">{info.content}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://caominyingyuan.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '关于我们',
              },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '关于我们 - 草民影院',
            description: '了解草民影院的品牌故事、发展历程、价值观和联系方式。',
            url: 'https://caominyingyuan.com/about',
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: aboutFAQs.map(faq => ({
              '@type': 'Question',
              name: faq.q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.a,
              },
            })),
          }),
        }}
      />
    </>
  );
}